import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useAuth } from '../stores/auth';
import {
  ArrowLeft,
  Search,
  PackageX,
  RotateCcw,
  Ban,
  MoreHorizontal,
  Camera,
  Check,
  X,
  AlertTriangle,
  ScanLine,
} from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

const RUPTURE_CAUSES = [
  { value: 'OUT_OF_STOCK', label: 'Sem Estoque', icon: PackageX, color: 'bg-red-100', iconColor: 'text-red-600' },
  { value: 'NOT_REPLENISHED', label: 'Nao Reposto', icon: RotateCcw, color: 'bg-orange-100', iconColor: 'text-orange-600' },
  { value: 'DISCONTINUED', label: 'Descontinuado', icon: Ban, color: 'bg-gray-100', iconColor: 'text-gray-600' },
  { value: 'OTHER', label: 'Outro', icon: MoreHorizontal, color: 'bg-blue-100', iconColor: 'text-blue-600' },
] as const;

interface Product {
  id: string;
  name: string;
  sku: string;
  brand_name?: string;
}

interface Rupture {
  id: string;
  product_name: string;
  product_sku?: string;
  cause: string;
  notes?: string;
  created_at: string;
}

export default function RuptureReport({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedCause, setSelectedCause] = useState<string>('');
  const [notes, setNotes] = useState('');

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Search products
  const { data: searchResults = [] } = useQuery<Product[]>({
    queryKey: ['products', 'search', searchTerm],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(searchTerm)}&limit=10`).then(res => res.data.data),
    enabled: searchTerm.length >= 2,
  });

  // Fetch existing ruptures for this visit
  const { data: existingRuptures = [] } = useQuery<Rupture[]>({
    queryKey: ['ruptures', visitId],
    queryFn: () => api.get(`/ruptures?visit_id=${visitId}`).then(res => res.data.data),
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/ruptures', {
        visit_id: visitId,
        store_id: storeId,
        promoter_id: user?.id, // Backend overrides this with the visit's promoter_id anyway
        product_id: selectedProduct?.id,
        cause: selectedCause,
        notes: notes || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      setSuccessMessage('Ruptura registrada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['ruptures', visitId] });
      // Reset form
      setSearchTerm('');
      setSelectedProduct(null);
      setSelectedCause('');
      setNotes('');
      setShowResults(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao registrar ruptura');
    },
  });

  const handleSubmit = () => {
    if (!selectedProduct) {
      alert('Selecione um produto');
      return;
    }
    if (!selectedCause) {
      alert('Selecione a causa da ruptura');
      return;
    }
    submitMutation.mutate();
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
  };

  const handleBarcodeScan = async (barcode: string) => {
    setShowScanner(false);
    try {
      const res = await api.get(`/products/barcode/${encodeURIComponent(barcode)}`);
      handleSelectProduct(res.data.data);
    } catch {
      alert(`Produto não encontrado para código: ${barcode}`);
    }
  };

  const handlePhotoCapture = () => {
    setLocation(`/camera/${visitId}?type=RUPTURE`);
  };

  const getCauseLabel = (cause: string) => {
    const found = RUPTURE_CAUSES.find(c => c.value === cause);
    return found ? found.label : cause;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Registrar Ruptura</h1>
          </div>
        </div>
      </header>

      {/* Success Toast */}
      {successMessage && (
        <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 p-4 space-y-6 pb-8">
        {/* Product Search */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Produto</h3>

          {selectedProduct ? (
            <div className="bg-white border border-primary/30 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-500 mt-1">SKU: {selectedProduct.sku}</p>
                  {selectedProduct.brand_name && (
                    <p className="text-sm text-gray-500">Marca: {selectedProduct.brand_name}</p>
                  )}
                </div>
                <button
                  onClick={handleClearProduct}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowResults(true);
                    }}
                    onFocus={() => setShowResults(true)}
                    placeholder="Buscar produto por nome ou SKU..."
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowScanner(true)}
                  className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-xl active:bg-primary/90"
                  title="Escanear código de barras"
                >
                  <ScanLine className="h-5 w-5" />
                </button>
              </div>

              {/* Search Results Dropdown */}
              {showResults && searchTerm.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Nenhum produto encontrado
                    </div>
                  ) : (
                    searchResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          SKU: {product.sku}
                          {product.brand_name && ` | ${product.brand_name}`}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cause Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Causa da Ruptura</h3>
          <div className="grid grid-cols-2 gap-3">
            {RUPTURE_CAUSES.map((cause) => {
              const Icon = cause.icon;
              const isSelected = selectedCause === cause.value;
              return (
                <button
                  key={cause.value}
                  onClick={() => setSelectedCause(cause.value)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className={`p-2 rounded-full ${cause.color}`}>
                    <Icon className={`h-5 w-5 ${cause.iconColor}`} />
                  </div>
                  <span className={`text-sm font-medium ${
                    isSelected ? 'text-primary' : 'text-gray-700'
                  }`}>
                    {cause.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Photo Capture */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Foto</h3>
          <button
            onClick={handlePhotoCapture}
            className="w-full flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-300 bg-white"
          >
            <Camera className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-500">Tirar foto da ruptura</span>
          </button>
          <p className="text-xs text-gray-400 mt-1">A foto será vinculada à visita automaticamente</p>
        </div>

        {/* Notes */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Observacoes (opcional)</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observacoes sobre a ruptura..."
            rows={3}
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitMutation.isPending || !selectedProduct || !selectedCause}
          className="w-full flex items-center justify-center gap-2 p-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Registrando...</span>
            </>
          ) : (
            <span>Registrar Ruptura</span>
          )}
        </button>

        {/* Existing Ruptures List */}
        {existingRuptures.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Rupturas Registradas ({existingRuptures.length})
            </h3>
            <div className="space-y-2">
              {existingRuptures.map((rupture) => (
                <div
                  key={rupture.id}
                  className="bg-white rounded-xl border border-gray-100 p-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-red-100 mt-0.5">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {rupture.product_name}
                      </p>
                      {rupture.product_sku && (
                        <p className="text-xs text-gray-500">SKU: {rupture.product_sku}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                          {getCauseLabel(rupture.cause)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(rupture.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {rupture.notes && (
                        <p className="text-xs text-gray-500 mt-1">{rupture.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
